import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchInvoices } from '../store/slices/invoiceSlice';
import { BRAND } from '../theme';
import { formatCurrency } from '../utils/money';
import { uniqueDestinations } from '../utils/destinations';
import { computeReport, ReportStatus } from '../utils/reportMetrics';
import {
  ArchiveCriteria,
  ALL_TIME,
  MONTH_NAMES,
  describeCriteria,
  matchesArchiveCriteria,
  yearsWithLoads
} from '../services/archiveExport';
import ArchiveDialog from '../components/reports/ArchiveDialog';
import { DriverDirectory, driverInfoFor } from '../components/pendingLoads/types';

/**
 * Read-only reporting over every saved load: totals, status mix, pending aging,
 * monthly billing and per-broker / per-driver breakdowns, plus the archive export.
 */

const STATUS_STYLE: Record<ReportStatus, { label: string; background: string; text: string; hint: string }> = {
  pending: {
    label: 'Pending',
    background: BRAND.pending.background,
    text: BRAND.pending.text,
    hint: 'Not yet paid out'
  },
  pay_to_driver: {
    label: 'Pay to Driver',
    background: BRAND.payToDriver.background,
    text: BRAND.payToDriver.text,
    hint: 'In the payout queue'
  },
  PAID: {
    label: 'Paid',
    background: BRAND.paid.background,
    text: BRAND.paid.text,
    hint: 'Driver paid'
  }
};

// Aging is a magnitude scale: one hue, light to dark as invoices get older.
const AGING_SHADES = ['#9DBEF2', '#5B93E6', '#1565D8', '#0B3C91'];

const MONTHS_SHOWN = 12;

const numberFormat = new Intl.NumberFormat('en-US');

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode }> = ({
  title,
  subtitle,
  children,
  action
}) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
      <Box>
        <Typography variant="subtitle1">{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    {children}
  </Paper>
);

const KpiTile: React.FC<{ label: string; value: string; detail?: string }> = ({ label, value, detail }) => (
  <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, minWidth: 0 }}>
    <Typography variant="body2" color="text.secondary" noWrap>
      {label}
    </Typography>
    <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, lineHeight: 1.25, mt: 0.5 }} noWrap>
      {value}
    </Typography>
    {detail && (
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
        {detail}
      </Typography>
    )}
  </Paper>
);

/** A labelled horizontal bar: label and value in ink, the bar carries the size. */
const BarRow: React.FC<{
  label: React.ReactNode;
  value: string;
  detail: string;
  fraction: number;
  color: string;
  tooltip: string;
}> = ({ label, value, detail, fraction, color, tooltip }) => (
  <Tooltip title={tooltip} placement="top">
    <Box sx={{ py: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
        <Box sx={{ minWidth: 0 }}>{label}</Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography component="span" variant="subtitle2">
            {value}
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
            {detail}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, backgroundColor: '#EEF2F7', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${Math.max(fraction > 0 ? 2 : 0, fraction * 100)}%`,
            backgroundColor: color,
            borderRadius: 4,
            transition: 'width 300ms'
          }}
        />
      </Box>
    </Box>
  </Tooltip>
);

const Reports: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { items: invoices, loading, error } = useAppSelector((state) => state.invoices);

  const isAdmin = user?.accountType === 'admin';
  const canArchive = user?.accountType === 'admin' || user?.accountType === 'owner';

  const [criteria, setCriteria] = useState<ArchiveCriteria>(ALL_TIME);
  const [broker, setBroker] = useState('all');
  const [driverDirectory, setDriverDirectory] = useState<DriverDirectory>({});
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const notify = useCallback(
    (message: string, severity: 'success' | 'error' | 'info' = 'success') =>
      setSnackbar({ open: true, message, severity }),
    []
  );

  const reload = useCallback(() => {
    if (user) {
      dispatch(fetchInvoices({ isAdmin: user.accountType === 'admin', dogtag: user.dogtag }));
    }
  }, [dispatch, user]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Driver names for the payout table (read only).
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    getDocs(collection(db, 'users'))
      .then((snapshot) => {
        if (cancelled) return;
        const directory: DriverDirectory = {};
        snapshot.docs.forEach((userDoc) => {
          const data = userDoc.data();
          if (!data.dogtag) return;
          directory[data.dogtag] = {
            dogtag: data.dogtag,
            name: data.name || data.displayName || data.dogtag,
            percentageCut: data.percentageCut || 0
          };
        });
        setDriverDirectory(directory);
      })
      .catch((loadError) => console.error('Error loading driver directory:', loadError));
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  /* -------------------------------- filtering -------------------------------- */

  const years = useMemo(() => yearsWithLoads(invoices), [invoices]);
  const destinations = useMemo(() => uniqueDestinations(invoices), [invoices]);
  const brokerOptions = useMemo(
    () =>
      Array.from(new Set(invoices.map((invoice: any) => (invoice.broker || '').trim()).filter(Boolean))).sort(),
    [invoices]
  );

  const filtered = useMemo(
    () =>
      invoices.filter(
        (invoice: any) =>
          matchesArchiveCriteria(invoice, criteria) && (broker === 'all' || invoice.broker === broker)
      ),
    [invoices, criteria, broker]
  );

  const report = useMemo(() => computeReport(filtered), [filtered]);

  const recentMonths = report.months.slice(-MONTHS_SHOWN);
  const maxMonth = Math.max(1, ...recentMonths.map((month) => month.amount));
  const maxStatus = Math.max(1, ...report.statuses.map((slice) => slice.amount));
  const maxAging = Math.max(1, ...report.aging.map((bucket) => bucket.amount));
  const pendingSlice = report.statuses.find((slice) => slice.status === 'pending');

  const filtersActive =
    criteria.year !== 'all' || criteria.month !== 'all' || criteria.destination.trim() !== '' || broker !== 'all';

  /* --------------------------------- render ---------------------------------- */

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1440, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2
        }}
      >
        <Box>
          <Typography variant="h5" component="h1">
            Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Payout, aging and broker reporting across every saved load.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={reload} disabled={loading}>
            Refresh
          </Button>
          {canArchive && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ArchiveOutlinedIcon />}
              onClick={() => setArchiveOpen(true)}
              disabled={invoices.length === 0}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              Generate for Archive
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr)) auto' },
            gap: 1.5,
            alignItems: 'center'
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel id="report-year">Year</InputLabel>
            <Select
              labelId="report-year"
              label="Year"
              value={criteria.year === 'all' ? 'all' : String(criteria.year)}
              onChange={(e) =>
                setCriteria((prev) => ({ ...prev, year: e.target.value === 'all' ? 'all' : Number(e.target.value) }))
              }
            >
              <MenuItem value="all">All years</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={String(year)}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="report-month">Month</InputLabel>
            <Select
              labelId="report-month"
              label="Month"
              value={criteria.month === 'all' ? 'all' : String(criteria.month)}
              onChange={(e) =>
                setCriteria((prev) => ({ ...prev, month: e.target.value === 'all' ? 'all' : Number(e.target.value) }))
              }
            >
              <MenuItem value="all">All months</MenuItem>
              {MONTH_NAMES.map((name, index) => (
                <MenuItem key={name} value={String(index)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            size="small"
            options={destinations}
            inputValue={criteria.destination}
            onInputChange={(_, value) => setCriteria((prev) => ({ ...prev, destination: value }))}
            renderInput={(params) => <TextField {...params} label="Destination" placeholder="Any" />}
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="report-broker">Broker</InputLabel>
            <Select labelId="report-broker" label="Broker" value={broker} onChange={(e) => setBroker(e.target.value)}>
              <MenuItem value="all">All brokers</MenuItem>
              {brokerOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            onClick={() => {
              setCriteria(ALL_TIME);
              setBroker('all');
            }}
            disabled={!filtersActive}
            sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && invoices.length === 0 ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Showing {numberFormat.format(report.loads)} load{report.loads === 1 ? '' : 's'} ·{' '}
            {describeCriteria(criteria)}
            {broker !== 'all' ? ` · ${broker}` : ''}
          </Typography>

          {/* Headline numbers */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 1.5,
              mb: 2
            }}
          >
            <KpiTile
              label="Total billed"
              value={formatCurrency(report.billed)}
              detail={`${formatCurrency(report.averageLoad)} avg per load`}
            />
            <KpiTile label="Loads" value={numberFormat.format(report.loads)} detail="All statuses" />
            <KpiTile
              label="Pending"
              value={formatCurrency(pendingSlice?.amount || 0)}
              detail={`${pendingSlice?.count || 0} loads · ${report.averagePendingDays} days avg age`}
            />
            <KpiTile
              label="Gallons hauled"
              value={numberFormat.format(report.gallons)}
              detail={`${formatCurrency(report.demurrage)} demurrage`}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mb: 2
            }}
          >
            {/* Status mix */}
            <Section title="By status" subtitle="Where every load stands in the payout flow">
              {report.statuses.map((slice) => {
                const style = STATUS_STYLE[slice.status];
                return (
                  <BarRow
                    key={slice.status}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: 12,
                            fontWeight: 700,
                            backgroundColor: style.background,
                            color: style.text
                          }}
                        >
                          {style.label}
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {style.hint}
                        </Typography>
                      </Box>
                    }
                    value={formatCurrency(slice.amount)}
                    detail={`${slice.count} loads`}
                    fraction={slice.amount / maxStatus}
                    color={style.text}
                    tooltip={`${style.label}: ${slice.count} loads, ${formatCurrency(slice.amount)}`}
                  />
                );
              })}
            </Section>

            {/* Aging */}
            <Section
              title="Pending aging"
              subtitle={`How long pending loads have been on the books · ${report.averagePendingDays} days on average`}
            >
              {report.aging.map((bucket, index) => (
                <BarRow
                  key={bucket.label}
                  label={<Typography variant="body2">{bucket.label}</Typography>}
                  value={formatCurrency(bucket.amount)}
                  detail={`${bucket.count} loads`}
                  fraction={bucket.amount / maxAging}
                  color={AGING_SHADES[index]}
                  tooltip={`${bucket.label}: ${bucket.count} pending loads, ${formatCurrency(bucket.amount)}`}
                />
              ))}
            </Section>
          </Box>

          {/* Monthly billing */}
          <Box sx={{ mb: 2 }}>
            <Section
              title="Billed by month"
              subtitle={
                report.months.length > MONTHS_SHOWN
                  ? `Last ${MONTHS_SHOWN} months with loads, by load date`
                  : 'By load date'
              }
            >
              {recentMonths.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No loads in this view.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto', mx: -1, px: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 0.5,
                      height: 200,
                      minWidth: recentMonths.length * 44,
                      borderBottom: `1px solid ${BRAND.border}`
                    }}
                  >
                    {recentMonths.map((month) => (
                      <Tooltip
                        key={month.key}
                        title={`${month.label}: ${formatCurrency(month.amount)} · ${month.count} loads`}
                        placement="top"
                      >
                        <Box
                          sx={{
                            flex: 1,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            cursor: 'default',
                            '&:hover .bar': { backgroundColor: BRAND.navy }
                          }}
                        >
                          <Box
                            className="bar"
                            sx={{
                              width: '100%',
                              maxWidth: 40,
                              height: `${Math.max(2, (month.amount / maxMonth) * 100)}%`,
                              backgroundColor: BRAND.blue,
                              borderRadius: '4px 4px 0 0',
                              transition: 'background-color 150ms'
                            }}
                          />
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: recentMonths.length * 44, mt: 0.75 }}>
                    {recentMonths.map((month) => (
                      <Typography
                        key={month.key}
                        variant="caption"
                        color="text.secondary"
                        sx={{ flex: 1, textAlign: 'center', whiteSpace: 'nowrap' }}
                      >
                        {month.label}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Section>
          </Box>

          {/* Broker breakdown */}
          <Box sx={{ mb: 2 }}>
            <Section title="By broker" subtitle="Billed per broker, split by status">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Broker</TableCell>
                      <TableCell align="right">Loads</TableCell>
                      <TableCell align="right">Billed</TableCell>
                      <TableCell align="right">Pending</TableCell>
                      <TableCell align="right">Pay to Driver</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Avg pending age</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.brokers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ color: 'text.secondary' }}>
                          No loads in this view.
                        </TableCell>
                      </TableRow>
                    )}
                    {report.brokers.map((row) => (
                      <TableRow key={row.broker} hover>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.broker}</TableCell>
                        <TableCell align="right">{row.loads}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(row.billed)}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(row.pending)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.payToDriver)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.paid)}</TableCell>
                        <TableCell align="right">
                          {row.averagePendingDays === null ? '—' : `${row.averagePendingDays} days`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Section>
          </Box>

          {/* Driver payouts */}
          {isAdmin && (
            <Section title="By driver" subtitle="Billed per driver, before the driver's percentage cut">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Driver</TableCell>
                      <TableCell align="right">Loads</TableCell>
                      <TableCell align="right">Billed</TableCell>
                      <TableCell align="right">Pending</TableCell>
                      <TableCell align="right">Pay to Driver</TableCell>
                      <TableCell align="right">Paid</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.drivers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ color: 'text.secondary' }}>
                          No loads in this view.
                        </TableCell>
                      </TableRow>
                    )}
                    {report.drivers.map((row) => (
                      <TableRow key={row.dogtag || 'unassigned'} hover>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {driverInfoFor(driverDirectory, row.dogtag).name}
                        </TableCell>
                        <TableCell align="right">{row.loads}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(row.billed)}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(row.pending)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.payToDriver)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.paid)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Section>
          )}
        </>
      )}

      {canArchive && (
        <ArchiveDialog
          open={archiveOpen}
          onClose={() => setArchiveOpen(false)}
          invoices={invoices}
          years={years}
          destinations={destinations}
          initialCriteria={criteria}
          onNotify={notify}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reports;
